import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'

import { emailRegex, htmlToPlainText, isValidWebsiteURL } from '@percent/utility'
import { TimePickerHelpers } from '@percent/domain/giving'

export const DEFAULT_MAX_STRING_LENGTH = 255
export const DEFAULT_URL_MAX_LENGTH = 2048
export const ORGANISATION_WEBSITE_MAX_LENGTH = 255
export const DEFAULT_RICH_TEXT_MAX_LENGTH = 5000
export const DEFAULT_RICH_TEXT_MAX_HTML_LENGTH = 50000

type ValidateRichTextOptions = {
  maxLength?: number
  maxHtmlLength?: number
  optional?: boolean
}

export const checkIfDateIsInTheFuture = (date: Date) => dayjs(date).isAfter(dayjs())

export const checkIfEndDateIsAfterStart = (start: Date, end: Date) => dayjs(end).isAfter(dayjs(start))

export const checkTimeFormatValidForFurtherChecks = (time: string) =>
  !!time.match(/^(?:\d?\d):[0-5]\d(?:\s?[apAP][mM])?$/)

export function useSharedValidationRules() {
  const { t } = useTranslation()

  const validateString = ({
    maxLength = DEFAULT_MAX_STRING_LENGTH,
    optional = false
  }: {
    maxLength?: number
    optional?: boolean
  } = {}) =>
    Yup.string()
      .transform(value => {
        if (typeof value !== 'string') return value

        return value.trim()
      })
      .when([], {
        is: () => !!maxLength,
        then: schema =>
          schema.max(
            maxLength,
            t('workplace_giving.validation.maxCharacters', {
              max: maxLength
            })
          )
      })
      .when([], {
        is: () => !optional,
        then: schema => schema.required(t('workplace_giving.validation.requiredField'))
      })

  const validateRichText = ({
    maxLength = DEFAULT_RICH_TEXT_MAX_LENGTH,
    maxHtmlLength = DEFAULT_RICH_TEXT_MAX_HTML_LENGTH,
    optional = false
  }: ValidateRichTextOptions = {}) =>
    Yup.string()
      .max(maxHtmlLength, t('workplace_giving.validation.tooMuchFormatting'))
      .test(
        'rich-text-visible-length',
        t('workplace_giving.validation.maxCharacters', { max: maxLength }),
        value => htmlToPlainText(value).length <= maxLength
      )
      .when([], {
        is: () => !optional,
        then: schema =>
          schema
            .required(t('workplace_giving.validation.requiredField'))
            .test(
              'rich-text-not-blank',
              t('workplace_giving.validation.requiredField'),
              value => !!htmlToPlainText(value).trim()
            )
      })

  const validateDate = ({
    shouldBeInTheFuture = false,
    optional = false
  }: {
    shouldBeInTheFuture?: boolean
    optional?: boolean
  } = {}) =>
    Yup.date()
      .nullable()
      .when([], {
        is: () => shouldBeInTheFuture,
        then: schema =>
          schema.test('startDateInTheFuture', t('workplace_giving.validation.dateInTheFuture'), value => {
            if (value) {
              return checkIfDateIsInTheFuture(value)
            }

            return true
          })
      })
      .when([], {
        is: () => !optional,
        then: Yup.date().required(t('workplace_giving.validation.requiredField'))
      })

  const validateEmail = (optional = false) =>
    Yup.string()
      .trim()
      .max(255, t('workplace_giving.validation.maxCharacters', { max: 255 }))
      .matches(emailRegex, t('workplace_giving.validation.invalidEmailAddress'))
      .when([], {
        is: () => !optional,
        then: schema => schema.required(t('workplace_giving.validation.requiredField'))
      })

  const validateUrl = (optional = false, maxLength = DEFAULT_URL_MAX_LENGTH) =>
    isValidWebsiteURL(t('workplace_giving.validation.url'))
      .trim()
      .max(maxLength, t('workplace_giving.validation.maxCharacters', { max: maxLength }))
      .when([], {
        is: () => !optional,
        then: schema => schema.required(t('workplace_giving.validation.requiredField'))
      })

  const validateNumber = ({
    min,
    max,
    mustBePositive = true,
    optional = false,
    canEqualMax = false
  }: {
    min?: number
    max?: number
    mustBePositive?: boolean
    optional?: boolean
    canEqualMax?: boolean
  } = {}) =>
    Yup.number()
      .nullable()
      .when([], {
        is: () => mustBePositive,
        then: schema => schema.positive(t('workplace_giving.validation.positive'))
      })
      .when([], {
        is: () => typeof min === 'number',
        then: schema => schema.min(min!, t('workplace_giving.validation.minAmount', { min }))
      })
      .when([], {
        is: () => typeof max === 'number',
        then: schema =>
          schema.max(
            max!,
            canEqualMax
              ? t('workplace_giving.validation.maxOrEqualAmount', { max })
              : t('workplace_giving.validation.maxAmount', { max })
          )
      })
      .when([], {
        is: () => !optional,
        then: schema => schema.required(t('workplace_giving.validation.requiredField'))
      })

  const validateTimeString = () =>
    Yup.string()
      .trim()
      .test('isValidTime', t('workplace_giving.validation.timeFormat'), (value: string | undefined) => {
        if (value) {
          const is12HrsFormat = TimePickerHelpers.is12hourFormat()

          return is12HrsFormat ? TimePickerHelpers.isValid12HourTime(value) : TimePickerHelpers.isValid24HourTime(value)
        }

        return true
      })

  return {
    validateString,
    validateRichText,
    validateDate,
    validateEmail,
    validateUrl,
    validateNumber,
    validateTimeString
  }
}
